import {type LatestPeakLoadWindow} from '@domain/electricity'
import {longDayNameAndTimeRangeFormatter} from '@hconnect/common/dates'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone} from '@hconnect/upm'
import moment, {Moment} from 'moment'
import {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ENERGY_CONSUMPTION_CHART_WIDGET_NAMESPACE} from '../consts'

import {AlertWithLeftBorder} from './AlertWithLeftBorder'

interface PeakLoadWindowAlertProps {
  peakLoadWindows?: LatestPeakLoadWindow[]
  startOfPlan: Moment
  endOfPlan: Moment
}

const usePeakLoadWindowAlertMessage = (closestPeakLoadWindow?: LatestPeakLoadWindow) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(ENERGY_CONSUMPTION_CHART_WIDGET_NAMESPACE)

  const timezoneId = useUpmPlantTimezone()

  return useMemo(() => {
    if (!closestPeakLoadWindow) return ''

    const formattedTimeFrame = longDayNameAndTimeRangeFormatter(
      [moment.utc(closestPeakLoadWindow.start), moment.utc(closestPeakLoadWindow.end)],
      timezoneId,
      language
    )

    return t('alerts.peakLoadWindow', {
      timeframe: formattedTimeFrame
    })
  }, [closestPeakLoadWindow, language, timezoneId, t])
}

export const PeakLoadWindowAlert: React.FC<PeakLoadWindowAlertProps> = ({
  peakLoadWindows,
  startOfPlan,
  endOfPlan
}) => {
  const currentDate = moment.utc()
  const startOfPlanUTC = startOfPlan.clone().utc()
  const endOfPlanUTC = endOfPlan.clone().utc()

  const closestPeakLoadWindow = peakLoadWindows?.find((window) => {
    return moment.utc(window.start).isAfter(currentDate)
  })

  const shouldShowAlert = !!(
    currentDate.isBetween(startOfPlanUTC, endOfPlanUTC, undefined, '[]') && closestPeakLoadWindow
  )

  const [showPeakLoadWindowAlert, setShowPeakLoadWindowAlert] = useState(shouldShowAlert)

  const peakLoadWindowAlertMessage = usePeakLoadWindowAlertMessage(closestPeakLoadWindow)

  useEffect(() => {
    setShowPeakLoadWindowAlert(shouldShowAlert)
  }, [shouldShowAlert])

  if (showPeakLoadWindowAlert) {
    return (
      <AlertWithLeftBorder
        severity="error"
        onClose={() => setShowPeakLoadWindowAlert(false)}
        {...dataTestId('peak-load-window-alert')}
      >
        {peakLoadWindowAlertMessage}
      </AlertWithLeftBorder>
    )
  }

  return null
}
